<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachmentFile: null,
      attachmentPreviewUrl: '',
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required: requiredIf(function attachmentIsMissing() {
        return !this.attachmentFile;
      }),
    },
  },
  methods: {
    resetForm() {
      this.revokeAttachmentPreview();
      this.shortCode = '';
      this.content = '';
      this.attachmentFile = null;
      this.attachmentPreviewUrl = '';
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          attachment: this.attachmentFile,
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
    onAttachmentChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.revokeAttachmentPreview();
      this.attachmentFile = file;
      this.attachmentPreviewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : '';
      this.v$.content.$touch();
    },
    removeAttachment() {
      this.revokeAttachmentPreview();
      this.attachmentFile = null;
      this.attachmentPreviewUrl = '';
      this.v$.content.$touch();
    },
    revokeAttachmentPreview() {
      if (this.attachmentPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.attachmentPreviewUrl);
      }
    },
  },
  beforeUnmount() {
    this.revokeAttachmentPreview();
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ATTACHMENT.LABEL') }}
            <span class="text-n-slate-10 font-normal">
              {{ $t('CANNED_MGMT.ATTACHMENT.OPTIONAL') }}
            </span>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              @change="onAttachmentChange"
            />
          </label>
          <div
            v-if="attachmentFile"
            class="mt-2 flex items-center gap-3 rounded-lg border border-n-weak bg-n-slate-2 p-3"
          >
            <img
              v-if="attachmentPreviewUrl"
              :src="attachmentPreviewUrl"
              :alt="attachmentFile.name"
              class="size-12 rounded object-cover"
            />
            <span v-else class="text-xl">📄</span>
            <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
              {{ attachmentFile.name }}
            </span>
            <NextButton
              ghost
              slate
              xs
              icon="i-lucide-x"
              :label="$t('CANNED_MGMT.ATTACHMENT.REMOVE')"
              @click.prevent="removeAttachment"
            />
          </div>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ $t('CANNED_MGMT.ATTACHMENT.HELP') }}
          </p>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
